import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useApiQuery } from "@/api/useApi";
import type { CurrentUser, ReportSummary } from "@/api/types";
import { ApiError, signingInWouldFix } from "@/api/client";
import { actingAs, workFor } from "@/api/actingAs";
import { buildLoginRequest } from "@/auth/authConfig";
import { useAuthSettings } from "@/auth/authSettingsContext";
import { forgetSignIn } from "@/auth/session";
import { InstallPrompt, InstallSteps } from "@/components/InstallPrompt";
import { useInstallOffer } from "@/install";
import { Icon } from "@/components/Icon";
import { Sheet } from "@/components/Sheet";
import { AllReportsPage } from "@/pages/AllReportsPage";
import { HomePage } from "@/pages/HomePage";
import { NewReportPage } from "@/pages/NewReportPage";
import { ReportPage } from "@/pages/ReportPage";
import { InboxPage } from "@/pages/InboxPage";
import { PayrollExportPage } from "@/pages/PayrollExportPage";
import { TipsSwitch } from "@/components/TipsSwitch";
import { devPreview } from "@/features";
import { InstructionsPage } from "@/pages/InstructionsPage";
import { GlReportPage } from "@/pages/GlReportPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { CategoriesPage } from "@/pages/CategoriesPage";
import { DelegatesPage } from "@/pages/DelegatesPage";

export function App() {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) return <SignIn />;
  return <Shell />;
}

/**
 * The app's mark, the PA receipt: on the teal header with the PlanetART wordmark beside it where
 * there's room, and as the rounded app icon on the white sign-in and loading screens, where the
 * cream receipt alone would disappear.
 */
function BrandMark({ onDark = false }: { onDark?: boolean }) {
  return (
    <span className={`brand-logo ${onDark ? "on-dark" : ""}`}>
      {onDark ? (
        <>
          <img className="logo-receipt" src="/logo-receipt.svg" alt="PA Expenses" />
          <img className="logo-wide" src="/logo-wide-white.svg" alt="PlanetART" />
        </>
      ) : (
        <img className="logo-icon" src="/icon.svg" alt="PA Expenses" />
      )}
    </span>
  );
}

function NoExpenseAccess({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="centered">
      <div className="panel">
        <BrandMark />
        <h1>You don’t have access yet</h1>
        <p className="muted">
          Expenses is open to people in the expense reporting group. Ask IT to add you, then sign in
          again.
        </p>
        <button className="btn secondary" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function SignIn() {
  const { instance } = useMsal();
  const settings = useAuthSettings();
  const [busy, setBusy] = useState(false);

  return (
    <div className="centered">
      <div className="panel">
        <BrandMark />
        <h1>PlanetArt Expenses</h1>
        <p className="muted">
          Snap receipts, log mileage and send reports to your manager — from your phone.
        </p>
        <button
          className="btn"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            instance.loginRedirect(buildLoginRequest(settings)).catch(() => setBusy(false));
          }}
        >
          {busy ? "Redirecting…" : "Sign in with Microsoft"}
        </button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function Shell() {
  const { instance } = useMsal();
  const settings = useAuthSettings();
  const location = useLocation();
  const {
    data: user,
    error,
    loading,
    reload: reloadUser,
  } = useApiQuery<CurrentUser>("/api/expenses/me");
  const signOut = () => {
    // Signing out here is what ends staying signed in on this device.
    forgetSignIn();
    void instance.logoutRedirect();
  };

  if (loading && !user) {
    return (
      <div className="centered">
        <div className="loading">
          <BrandMark />
          <p className="muted">Signing you in…</p>
        </div>
      </div>
    );
  }

  // No longer a delegate of whoever this tab was working for: back to themselves.
  if (error instanceof ApiError && error.status === 403 && actingAs()) {
    workFor(null);
    return null;
  }

  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <NoExpenseAccess onSignOut={signOut} />;
  }

  // Microsoft couldn't renew the sign-in quietly (and the automatic retry already ran): a
  // button, not a library error, so a tap signs them straight back in.
  if (error && signingInWouldFix(error)) {
    return (
      <div className="centered">
        <div className="panel">
          <BrandMark />
          <h1>Please sign in again</h1>
          <p className="muted">Your sign-in needs refreshing. It only takes a moment.</p>
          <button
            className="btn"
            onClick={() => {
              const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
              void instance.loginRedirect({
                ...buildLoginRequest(settings),
                ...(account ? { loginHint: account.username } : {}),
              });
            }}
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="centered">
        <div className="panel">
          <h1>Something went wrong</h1>
          <div className="error">{error?.message ?? "Could not load your profile."}</div>
          <button className="btn secondary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user.roles.includes("ExpenseUser")) {
    return <NoExpenseAccess onSignOut={signOut} />;
  }

  return (
    <Signedin
      user={user}
      pathname={location.pathname}
      onSignOut={signOut}
      onProfileChanged={reloadUser}
    />
  );
}

function Signedin({
  user,
  pathname,
  onSignOut,
  onProfileChanged,
}: {
  user: CurrentUser;
  pathname: string;
  onSignOut: () => void;
  /** Settings can change who someone's reports go to, which their profile carries. */
  onProfileChanged: () => void;
}) {
  const isPayroll = user.roles.includes("Payroll");
  const isAdmin = user.roles.includes("ExpenseAdmin");
  const [account, setAccount] = useState(false);
  const [installing, setInstalling] = useState(false);
  const installOffer = useInstallOffer();

  // Nobody has a field saying they manage people, so the tab appears once there is something to
  // approve, and the count is refreshed whenever the screen changes.
  const { data: inbox, reload: reloadInbox } = useApiQuery<ReportSummary[]>(
    "/api/expenses/inbox/manager",
  );
  // Reports payroll asked this person to approve as an executive count as approvals too.
  const { data: executive, reload: reloadExecutive } = useApiQuery<ReportSummary[]>(
    "/api/expenses/inbox/executive",
  );
  // Payroll's count: reports ready for them, not those waiting on an executive.
  const { data: payrollQueue, reload: reloadPayroll } = useApiQuery<ReportSummary[]>(
    isPayroll ? "/api/expenses/inbox/payroll" : null,
  );
  const [shownPath, setShownPath] = useState(pathname);
  if (shownPath !== pathname) {
    setShownPath(pathname);
    reloadInbox();
    reloadExecutive();
    if (isPayroll) reloadPayroll();
  }
  const waiting = (inbox?.length ?? 0) + (executive?.length ?? 0);
  const payrollWaiting = (payrollQueue ?? []).filter((r) => r.status !== "ExecutiveReview").length;
  const showApprovals = waiting > 0 || pathname.startsWith("/inbox");
  // Working for someone as their delegate: everything is theirs until switching back.
  const acting = user.actingFor ? user : null;
  const delegators = user.delegators ?? [];
  const delegates = user.delegates ?? [];

  return (
    <div className="app">
      <header className="topbar">
        <Link className="brand" to="/" aria-label="PA Expenses, go to My Reports">
          <BrandMark onDark />
          <strong>Expenses</strong>
        </Link>
        <div className="topbar-actions">
          {devPreview && <TipsSwitch />}
          <button
            type="button"
            className={`avatar ${acting ? "acting" : ""}`}
            onClick={() => setAccount(true)}
            aria-label={acting ? `Working for ${user.displayName}` : `Account: ${user.displayName}`}
          >
            {initials(user.displayName)}
          </button>
        </div>
      </header>
      {acting && (
        <div className="acting-banner" role="status">
          <span>
            Working for <strong>{acting.displayName}</strong>
          </span>
          <button type="button" className="link-btn" onClick={() => workFor(null)}>
            Switch back to yourself
          </button>
        </div>
      )}

      <nav className="tabs" aria-label="Main">
        <NavLink to="/" end>
          <Icon name="reports" size={22} />
          <span>My Reports</span>
        </NavLink>
        <NavLink to="/all">
          <Icon name="search" size={22} />
          {/* "Search" alone on the phone's tab bar, where the full name doesn't fit. */}
          <span>
            <span className="wide-only">All Reports/</span>Search
          </span>
        </NavLink>
        {showApprovals && (
          <NavLink to="/inbox">
            <Icon name="approve" size={22} />
            <span>Approvals</span>
            {waiting > 0 && <span className="count">{waiting}</span>}
          </NavLink>
        )}
        {isPayroll && (
          <NavLink to="/payroll">
            <Icon name="pay" size={22} />
            <span>Payroll</span>
            {payrollWaiting > 0 && <span className="count">{payrollWaiting}</span>}
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/settings">
            <Icon name="policy" size={22} />
            <span>Settings</span>
          </NavLink>
        )}
      </nav>

      <main className="main">
        <Routes>
          <Route path="/" element={<HomePage userId={user.id} />} />
          <Route path="/new" element={<NewReportPage />} />
          <Route path="/all" element={<AllReportsPage />} />
          <Route path="/reports/:reportId" element={<ReportPage user={user} />} />
          <Route path="/inbox" element={<InboxPage kind="manager" />} />
          <Route path="/accounting" element={<Navigate to="/" replace />} />
          <Route
            path="/payroll"
            element={isPayroll ? <InboxPage kind="payroll" /> : <Navigate to="/" replace />}
          />
          <Route
            path="/payroll/export"
            element={isPayroll ? <PayrollExportPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/payroll/gl"
            element={isPayroll ? <GlReportPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings"
            element={
              isAdmin ? (
                <SettingsPage forwardTo={user.forwardTo} onSaved={onProfileChanged} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/settings/categories"
            element={isAdmin ? <CategoriesPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings/instructions"
            element={isAdmin ? <InstructionsPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings/delegates"
            element={isAdmin ? <DelegatesPage /> : <Navigate to="/" replace />}
          />
          <Route path="/policy" element={<Navigate to="/settings" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Sheet open={account} title="Account" onClose={() => setAccount(false)}>
        <div className="stack">
          <div className="account">
            <span className="avatar large">{initials(user.displayName)}</span>
            <div>
              <strong>{user.displayName}</strong>
              <span className="muted">{user.email ?? user.userName}</span>
            </div>
          </div>
          <dl className="facts">
            <div>
              <dt>Reports go to</dt>
              <dd>
                {acting
                  ? `${acting.displayName} to approve, then ${user.approver?.id === user.id ? "payroll" : (user.approver?.displayName ?? "payroll")}`
                  : (user.approver?.displayName ?? "Payroll (no manager on file)")}
              </dd>
            </div>
          </dl>
          {!acting && delegates.length > 0 && (
            <dl className="facts">
              <div>
                <dt>Your delegates</dt>
                {delegates.map((person) => (
                  <dd key={person.id}>
                    {person.displayName}
                    <span className="muted">
                      {" "}
                      · {person.canApprove ? "Expenses and approvals" : "Expenses"}
                    </span>
                  </dd>
                ))}
              </div>
            </dl>
          )}
          {delegators.length > 0 && (
            <div className="working-for">
              <strong>Working for</strong>
              <ul className="card choice-list">
                <li>
                  <button
                    type="button"
                    aria-current={acting ? undefined : "true"}
                    onClick={() => (acting ? workFor(null) : setAccount(false))}
                  >
                    <span>Myself</span>
                    {!acting && <Icon name="check" size={18} />}
                  </button>
                </li>
                {delegators.map((person) => {
                  const current = acting?.id === person.id;
                  return (
                    <li key={person.id}>
                      <button
                        type="button"
                        aria-current={current ? "true" : undefined}
                        onClick={() =>
                          current
                            ? setAccount(false)
                            : workFor({ id: person.id, displayName: person.displayName })
                        }
                      >
                        <span>
                          {person.displayName}
                          <span className="muted">
                            {person.canApprove ? "Expenses and approvals" : "Expenses"}
                          </span>
                        </span>
                        {current && <Icon name="check" size={18} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {installOffer && (
            <button
              type="button"
              className="btn secondary"
              onClick={() => {
                setAccount(false);
                setInstalling(true);
              }}
            >
              <Icon name="download" size={18} /> Install on this device
            </button>
          )}
          <button type="button" className="btn secondary" onClick={onSignOut}>
            <Icon name="logOut" size={18} /> Sign out
          </button>
        </div>
      </Sheet>
      <InstallPrompt />
      <Sheet
        open={installing && installOffer !== null}
        title="Install PA Expenses"
        onClose={() => setInstalling(false)}
      >
        {installOffer && (
          <div className="stack">
            <p className="muted">
              {installOffer.kind === "ios"
                ? "Add it to your Home Screen to open it like an app."
                : "Install it to open it like an app, without the browser around it."}
            </p>
            <InstallSteps offer={installOffer} onDone={() => setInstalling(false)} />
          </div>
        )}
      </Sheet>
    </div>
  );
}
